/**
 * The orchestration flow on Overview: the whole lifecycle Orgus can see, left to right. Projects
 * (with the branches in progress there), Agents (sessions by agent kind), Delivery (the landing
 * queue's stages) and Environments (what is deployed). Every agent joins the delivery stages its
 * projects' changes are in, so the picture reads as one path from a project to production. Two
 * readings of the same model: Overall, one graph across every project (or one, focused), and By
 * project, one slim lane per project across the same stages. Built from the live state the console
 * already holds; pure, so the nodes, edges, lanes, states and hues, the "+N more" folding and which
 * node or lane an event belongs to are all tested.
 */
import type { Checkout, DeployTarget, FeedEvent, Id, MachineLoadState, MissionState, NeedsYouSessionLevel, Pipeline, PipelineItem, Project, Session } from '@orgus/protocol';
import { AGENT_CHECKS_STAGE, LANDING_FATE, agentKindLabel, agentKindOf, pipelineKey } from '@orgus/protocol';
import { projectHue } from '@orgus/ui';
import type { Deployment, Landing, LandingState } from '@orgus/protocol';
import { getFleetCpuWaitingStats } from '../machine-load/cpuWaiting.ts';
import { runningUnits } from '../../lib/running.ts';
import { heldDeploy } from '../project-health/model.ts';
import { sameChange } from '../projects/work.ts';
import { worktreeLine, worktreeStates } from './worktrees.ts';
import type { WorktreeStates } from './worktrees.ts';

/** The four stages, left to right. Each column always shows, with a quiet line when it is empty. */
export const FLOW_STAGES = [
  { title: 'Projects', empty: 'No work in progress', href: '/projects' },
  { title: 'Agents', empty: 'No agents running', href: '/sessions' },
  { title: 'Delivery', empty: 'No landing queue yet', href: '/pipelines' },
  {
    title: 'Environments',
    empty: 'Nothing deployed yet',
    href: '/pipelines/environments',
  },
] as const;

export type FlowColumn = 0 | 1 | 2 | 3;

/** What a node or an edge carries, and so its colour: amber, red, cyan, green, blue-grey or grey. */
export type FlowState = 'attention' | 'danger' | 'working' | 'success' | 'pending' | 'idle';

export type DataSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
/** The icon tile's colour: a state, neutral, or a project's data slot. */
export type FlowHue = 'attention' | 'danger' | 'working' | 'success' | 'neutral' | DataSlot;

/** The one short line under a node's name: a count and its words ("2 need you"), or a phrase ("v2.1 live"). */
export interface FlowFact {
  n?: number;
  text: string;
  /** Only needs you and failed are coloured in text. */
  tone?: 'attention' | 'danger';
  /** A second, quieter count on the same line: "9 working, 14 subagents" (shown where the card has room, unless `keep`). */
  also?: { n: number; text: string; keep?: boolean };
}

export interface FlowNode {
  id: string;
  column: FlowColumn;
  /**
   * empty: a stage with nothing in it, still a quiet node in the chain so the flow reads end to end.
   * next: what is lined up (missions planned, backlog items ready), at the start of the chain.
   * missions: the missions under way, beside the agents that run them.
   */
  kind: 'project' | 'agent' | 'stage' | 'env' | 'more' | 'empty' | 'next' | 'missions';
  label: string;
  fact: FlowFact;
  /** The fact as text: "2 need you". */
  detail: string;
  state: FlowState;
  hue: FlowHue;
  /** An agent node's live sessions, shown beside its name. */
  count?: number;
  /** An agent node's kind: it shows the agent's own glyph and colour. */
  agentKind?: string;
  /** A project node's project: it shows the project's mark. */
  projectId?: Id;
  /** Production reads a little brighter than the other environments. */
  emphasis?: boolean;
  /** What this card finished in the window, at the end of its fact line: "12 finished today". */
  past?: { n: number; label: string };
  /** An agent kind with sessions at work: its mark breathes (the running indicator). */
  live?: boolean;
  /** The numbers behind the fact line, for the hover tooltip ("4 working", a change's title). */
  tip?: string[];
  /** How much is going on: the tie-break after state when a column is sorted. */
  weight: number;
  href: string;
}

export interface FlowEdge {
  from: string;
  to: string;
  /** What flows along it now: its colour. */
  state: FlowState;
  /** Work is moving along it now (drawn solid; quiet edges are dashed). */
  active: boolean;
}

export interface Flow {
  nodes: FlowNode[];
  edges: FlowEdge[];
  /** A node folded into its column's "+N more", by id: events for it go to the "+N more" node. */
  alias: Record<string, string>;
  /** Each column's size before folding: projects, live sessions, changes in flight, environments. */
  totals: [number, number, number, number];
}

export interface FlowInput {
  sessions: readonly Session[];
  projects: Record<string, Project>;
  pipelines: readonly Pipeline[];
  checkouts?: readonly Checkout[];
  landing?: LandingState | undefined;
  /** The needs-you selector's reading per session (blocked or asked); without it, a waiting session needs you. */
  needs?: ReadonlyMap<Id, NeedsYouSessionLevel>;
  /** The deploy targets projects report through the project protocol (deploys.json), as Environments lists them. */
  deploys?: readonly { projectId: Id; target: DeployTarget }[];
  /** Every mission as its live view reads it (Missions): its state, and when it finished. */
  missions?: readonly FlowMission[];
  /** The backlog items that are Ready to start (not epics or features): what is lined up. */
  ready?: readonly { projectId: Id }[];
  /** Every mission paused (ADR 0103): the Missions card says so and nothing new flows from Up next. */
  paused?: boolean;
  now?: number;
  /** The machines' work queues (ORGU-131): how many wait for CPU shows on the Queued stage. */
  machineLoad?: MachineLoadState;
}

/** A mission as the flow reads it: the state its live view derives, and when it finished. */
export interface FlowMission {
  id: Id;
  projectId: Id;
  state: MissionState;
  finishedAt: number | null;
  /** When Orgus landed its work on the project's main (worktree.integrate), else absent. */
  landedAt?: number | null;
}

/**
 * What finished counts in: since local midnight, the one window the whole flow names ("12 today").
 * An item with no end time is not counted: it cannot be placed in the window.
 */
export function windowStart(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
export const WINDOW_WORD = 'today';
export const inWindow = (at: number | null | undefined, now: number) => at != null && at >= windowStart(now) && at <= now + 60_000;

/**
 * A failed attempt that was submitted again is history, not a failure that stands: its newer
 * attempt speaks for it (the Projects page's rule, features/projects/work.ts).
 */
export function supersededFailure(i: Pick<PipelineItem, 'state' | 'title' | 'startedAt'>, all: readonly Pick<PipelineItem, 'title' | 'startedAt'>[]): boolean {
  return i.state === 'failed' && all.some((o) => o !== i && sameChange(o.title, i.title) && (o.startedAt ?? 0) > (i.startedAt ?? 0));
}

/** A column shows this many nodes; past it, the first ones and a "+N more". */
export const COLUMN_CAP: Record<FlowColumn, number> = {
  0: 5,
  1: 5,
  2: 5,
  3: 5,
};
/** By project shows this many lanes, then "+N more projects". */
export const MAX_LANES = 8;
const RANK: Record<FlowState, number> = {
  attention: 0,
  danger: 1,
  working: 2,
  pending: 3,
  success: 4,
  idle: 5,
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
/** "1 needs you", "2 need you". */
export const needsYouText = (n: number) => `${n} ${plural(n, 'needs you', 'need you')}`;
const needsYouFact = (n: number): FlowFact => ({
  n,
  text: plural(n, 'needs you', 'need you'),
  tone: 'attention',
});
const text = (f: FlowFact) => `${f.n == null ? f.text : `${f.n} ${f.text}`}${f.also ? `, ${f.also.n} ${f.also.text}` : ''}`;
export const worst = (a: FlowState, b: FlowState): FlowState => (RANK[a] <= RANK[b] ? a : b);
/** A state's tile colour: queued and quiet things are neutral. */
const toneOf = (s: FlowState): FlowHue => (s === 'pending' || s === 'idle' ? 'neutral' : s);

const ENV_ORDER = ['production', 'staging', 'preview'];
const envRank = (e: string) => {
  const i = ENV_ORDER.indexOf(e.toLowerCase());
  return i < 0 ? ENV_ORDER.length : i;
};
const envLabel = (e: string) => e.slice(0, 1).toUpperCase() + e.slice(1);

/** How a session reads in the flow: blocked on a person, asked a question, working or idle. */
type SessionRead = 'blocked' | 'asked' | 'working' | 'idle';
function sessionRead(s: Session, needs: FlowInput['needs']): SessionRead {
  const level = needs?.get(s.id);
  if (level === 'blocked') return 'blocked';
  if (level === 'asked') return 'asked';
  if (!needs && s.status === 'waiting') return 'blocked';
  return s.status === 'working' ? 'working' : 'idle';
}
const STATE_OF: Record<SessionRead, FlowState> = {
  blocked: 'attention',
  asked: 'idle',
  working: 'working',
  idle: 'idle',
};

/** Only what belongs to one project: the Overall view focused on it. */
export function focusInput(input: FlowInput, projectId: Id): FlowInput {
  const landing = input.landing;
  return {
    ...input,
    sessions: input.sessions.filter((s) => s.projectId === projectId),
    pipelines: input.pipelines.filter((p) => p.projectId === projectId),
    checkouts: input.checkouts?.filter((c) => c.projectId === projectId),
    landing: landing && {
      ...landing,
      queues: landing.queues.filter((q) => q.projectId === projectId),
      landings: landing.landings.filter((l) => l.projectId === projectId),
      deployments: landing.deployments.filter((d) => d.projectId === projectId),
      configs: landing.configs.filter((c) => c.projectId === projectId),
    },
    missions: input.missions?.filter((m) => m.projectId === projectId),
    ready: input.ready?.filter((i) => i.projectId === projectId),
    deploys: input.deploys?.filter((d) => d.projectId === projectId),
  };
}

/** Delivery as counts: what waits, what is verified now, what landed or failed today. */
export interface Delivery {
  queued: number;
  verifying: number;
  landed: number;
  failed: number;
  /** Parked landings: they wait on a person. */
  parked: number;
  /** Failures nobody has (tagged stuck, ORGU-83): they wait on a person too. */
  stuck: number;
}

/** A failure the branch landed after is settled (its fate tag): the flow does not count it (ORGU-83). */
/** Settled since it failed: landed (or its work reached main another way), or its branch was deleted. */
const landedSince = (i: PipelineItem): boolean => !!i.tags?.some((t) => t === LANDING_FATE.landedSince || t === LANDING_FATE.gone);

/** One source of delivery work (an adapter's pipeline, or the hub's landing queue) and its counts. */
export interface DeliverySource extends Delivery {
  /** pipelineKey, or 'landing' for the hub's landing queue. */
  key: string;
  projectId: Id;
  /** "Orgus landings". */
  label: string;
  /** Where its changes are listed: the pipeline on Pipelines, or the landing queue tab. */
  href: string;
}

/** A pipeline's changes by delivery stage, the way the Pipelines page reads them. */
function pipelineDelivery(p: Pipeline, now: number): Delivery {
  const out: Delivery = { queued: 0, verifying: 0, landed: 0, failed: 0, parked: 0, stuck: 0 };
  for (const i of p.items) {
    if (i.state === 'active') out.verifying++;
    else if (i.state === 'queued') out.queued++;
  }
  const all = [...p.items, ...p.recent];
  for (const i of p.recent) {
    if (!inWindow(i.endedAt, now)) continue;
    if (i.state === 'passed') out.landed++;
    else if (i.state === 'failed' && !landedSince(i) && !supersededFailure(i, all)) {
      out.failed++;
      if (i.tags?.includes(LANDING_FATE.stuck)) out.stuck++;
    }
  }
  return out;
}

/**
 * The repo folder a session works in: the part before `.claude/` for a worktree there
 * (`orgus/.claude/worktrees/x` is orgus), else the last folder of its working directory.
 */
export function repoFolderOf(cwd: string): string | null {
  const path = cwd.replace(/\\/g, '/').replace(/\/+$/, '');
  const cut = path.search(/\/\.claude\//);
  const root = cut >= 0 ? path.slice(0, cut) : path;
  return root.slice(root.lastIndexOf('/') + 1) || null;
}

/**
 * A project's name. A hub that has just restarted reports its sessions before its git scan
 * names their projects, so until then the folder most of the project's sessions work in names it.
 */
export function projectNameOf(input: Pick<FlowInput, 'projects' | 'sessions'>, id: Id): string {
  const name = input.projects[id]?.name;
  if (name) return name;
  const votes = new Map<string, number>();
  for (const s of input.sessions) {
    const folder = s.projectId === id && s.cwd ? repoFolderOf(s.cwd) : null;
    if (folder) votes.set(folder, (votes.get(folder) ?? 0) + 1);
  }
  const best = [...votes].sort((a, b) => b[1] - a[1])[0]?.[0];
  return best ?? 'Unknown project';
}

/** "Orgus landings": the project, then the pipeline's own title mid-sentence. */
function pipelineLabel(p: Pipeline, input: FlowInput): string {
  const title = p.title || 'Pipeline';
  return `${projectNameOf(input, p.projectId)} ${title.slice(0, 1).toLowerCase()}${title.slice(1)}`;
}

/** Every source of delivery work, with its counts: each adapter pipeline, then the hub's landing queue per project. */
export function deliverySources(input: FlowInput, projectId?: Id): DeliverySource[] {
  const now = input.now ?? Date.now();
  const mine = (pid: Id) => projectId === undefined || pid === projectId;
  const out: DeliverySource[] = [];
  for (const p of input.pipelines) {
    if (!mine(p.projectId)) continue;
    const key = pipelineKey(p);
    out.push({ key, projectId: p.projectId, label: pipelineLabel(p, input), href: `/pipelines?pipeline=${encodeURIComponent(key)}`, ...pipelineDelivery(p, now) });
  }
  const queues = new Map<Id, DeliverySource>();
  for (const l of input.landing?.landings ?? []) {
    if (!mine(l.projectId)) continue;
    let q = queues.get(l.projectId);
    if (!q) {
      q = { key: `landing:${l.projectId}`, projectId: l.projectId, label: `${projectNameOf(input, l.projectId)} landing queue`, href: '/pipelines/landing', queued: 0, verifying: 0, landed: 0, failed: 0, parked: 0, stuck: 0 };
      queues.set(l.projectId, q);
    }
    if (l.status === 'queued' || l.status === 'parked') {
      q.queued++;
      if (l.status === 'parked') q.parked++;
    } else if (l.status === 'active') q.verifying++;
    else if (l.status === 'landed' && inWindow(l.endedAt, now)) q.landed++;
    else if (l.status === 'failed' && inWindow(l.endedAt, now)) q.failed++;
  }
  return [...out, ...queues.values()];
}

export function deliveryOf(input: FlowInput, projectId?: Id): Delivery {
  const out: Delivery = { queued: 0, verifying: 0, landed: 0, failed: 0, parked: 0, stuck: 0 };
  for (const s of deliverySources(input, projectId)) for (const k of ['queued', 'verifying', 'landed', 'failed', 'parked', 'stuck'] as const) out[k] += s[k];
  return out;
}

/** One environment as the flow shows it: the landing module's environments and the projects' own deploy targets. */
interface EnvEntry {
  id: string;
  label: string;
  state: FlowState;
  fact: FlowFact;
  emphasis: boolean;
  /** Has anything been deployed there: a line into it carries the landed state. */
  deployed: boolean;
  /** Deploys that finished there today. */
  deploys: number;
  tip?: string[];
}

/** The newest deployment to each environment, production first. */
function landingEnvironments(input: FlowInput, projectId?: Id): Array<[string, Deployment | null]> {
  const envs = new Map<string, Deployment | null>();
  for (const d of input.landing?.deployments ?? []) if ((projectId === undefined || d.projectId === projectId) && !envs.has(d.env)) envs.set(d.env, d);
  for (const c of input.landing?.configs ?? []) {
    if (projectId !== undefined && c.projectId !== projectId) continue;
    for (const e of c.environments) if (!envs.has(e.name)) envs.set(e.name, null);
  }
  return [...envs].sort(([a], [b]) => envRank(a) - envRank(b) || a.localeCompare(b));
}

/** A deploy target from deploys.json, read the way Environments reads it. */
function targetRead(t: DeployTarget): { state: FlowState; fact: FlowFact } {
  const version = t.last?.version ?? t.mainVersion ?? t.live?.version ?? '';
  if (t.deploying) return { state: 'working', fact: { text: version ? `Deploying ${version}` : 'Deploying' } };
  if (t.last?.result === 'failed') return { state: 'danger', fact: { text: version ? `${version} failed` : 'Deploy failed', tone: 'danger' } };
  // Refused (main red) or cancelled for a newer version: what is live is not what was asked for.
  const held = heldDeploy(t);
  if (held) return { state: 'attention', fact: { text: t.live ? `${t.live.version} live, last deploy ${held}` : `Last deploy ${held}`, tone: 'attention' } };
  if (t.live) return { state: 'success', fact: { text: `${t.live.version} live` } };
  return { state: 'idle', fact: { text: 'Not deployed yet' } };
}

/** An environment's node id, from its name: one card per name, whichever system reports it. */
export const envNodeId = (name: string) => `env:${name.trim().toLowerCase().replace(/\s+/g, '-')}`;

/**
 * Every environment, production first: the landing module's (the newest deployment to each) and
 * each project's reported deploy targets, one card per name. Where two report the same name (two
 * projects' Production), the card shows what is moving or failed first, and its tooltip names each.
 */
function environmentsOf(input: FlowInput, projectId?: Id): EnvEntry[] {
  const byId = new Map<string, EnvEntry>();
  const projectName = (pid: Id) => projectNameOf(input, pid);
  const add = (e: EnvEntry) => {
    const prev = byId.get(e.id);
    if (!prev) return void byId.set(e.id, e);
    const loud = (x: EnvEntry) => (x.state === 'working' || x.state === 'danger' ? 2 : x.state === 'attention' ? 1 : 0);
    const lead = loud(e) > loud(prev) ? e : prev;
    byId.set(e.id, { ...lead, deployed: prev.deployed || e.deployed, emphasis: prev.emphasis || e.emphasis, deploys: prev.deploys + e.deploys, tip: [...(prev.tip ?? []), ...(e.tip ?? [])] });
  };
  const now = input.now ?? Date.now();
  const doneHere = (env: string) =>
    (input.landing?.deployments ?? []).filter((d) => (projectId === undefined || d.projectId === projectId) && d.env === env && (d.status === 'live' || d.status === 'failed') && inWindow(d.endedAt, now)).length;
  for (const [env, dep] of landingEnvironments(input, projectId)) {
    const { state, fact } = envRead(dep);
    add({ id: envNodeId(env), label: envLabel(env), state, fact, emphasis: env.toLowerCase() === 'production', deployed: !!dep, deploys: doneHere(env), tip: dep ? [`${projectName(dep.projectId)}: ${text(fact)}`] : undefined });
  }
  for (const { projectId: pid, target: t } of input.deploys ?? []) {
    if (projectId !== undefined && pid !== projectId) continue;
    const { state, fact } = targetRead(t);
    const tip = [`${projectName(pid)}: ${text(fact)}`];
    // Why the last deploy failed or did not happen, in the project's words.
    if (t.last?.message && (state === 'danger' || state === 'attention')) tip.push(t.last.message);
    if (t.behind && t.mainVersion) tip.push(`Main is at ${t.mainVersion}`);
    if (t.phase) tip.push(t.phase.slice(0, 1).toUpperCase() + t.phase.slice(1));
    // A refused or cancelled attempt deployed nothing: only a finished deploy counts.
    const deploys = t.last && (t.last.result === 'succeeded' || t.last.result === 'failed') && inWindow(t.last.at, now) ? 1 : 0;
    add({ id: envNodeId(t.label), label: t.label, state, fact, emphasis: t.label.toLowerCase() === 'production', deployed: !!t.live, deploys, tip });
  }
  return [...byId.values()].sort((a, b) => envRank(a.label) - envRank(b.label));
}

function envRead(d: Deployment | null): { state: FlowState; fact: FlowFact } {
  if (!d) return { state: 'idle', fact: { text: 'Not deployed yet' } };
  if (d.status === 'deploying') return { state: 'working', fact: { text: `Deploying ${d.version}` } };
  if (d.status === 'failed')
    return {
      state: 'danger',
      fact: { text: `${d.version} failed`, tone: 'danger' },
    };
  if (d.status === 'live') return { state: 'success', fact: { text: `${d.version} live` } };
  return { state: 'idle', fact: { text: d.version } };
}

/**
 * Each project's worktrees by what they need (the Overview project list's reading): active (a
 * session works or waits in it), to land, uncommitted. Clean, merged ones are not counted, and a
 * worktree nobody is on is not "in progress": only active ones are.
 */
function worktreesOf(input: FlowInput, live: readonly Session[]): Map<Id, WorktreeStates> {
  const byProject = new Map<Id, Checkout[]>();
  for (const c of input.checkouts ?? []) if (!c.error) byProject.set(c.projectId, [...(byProject.get(c.projectId) ?? []), c]);
  const out = new Map<Id, WorktreeStates>();
  for (const [id, cos] of byProject)
    out.set(
      id,
      worktreeStates(
        cos,
        live.filter((s) => s.projectId === id),
      ),
    );
  return out;
}

/**
 * The projects with anything going on: live sessions, active worktrees, delivery work, worktrees
 * waiting to land, or work finished today (a change landed, a session ended).
 */
function activeProjects(input: FlowInput, live: readonly Session[], trees: ReadonlyMap<Id, WorktreeStates>, finished: readonly Session[]): Set<Id> {
  const ids = new Set<Id>();
  for (const s of finished) if (s.projectId) ids.add(s.projectId);
  for (const s of deliverySources(input)) if (s.landed) ids.add(s.projectId);
  for (const s of live) if (s.projectId) ids.add(s.projectId);
  for (const [id, w] of trees) if (w.active + w.toLand + w.uncommitted > 0) ids.add(id);
  for (const p of input.pipelines) if (p.items.length) ids.add(p.projectId);
  for (const l of input.landing?.landings ?? []) if (l.status === 'queued' || l.status === 'active' || l.status === 'parked') ids.add(l.projectId);
  for (const id of ids) if (!nameable(input, id)) ids.delete(id);
  return ids;
}

/**
 * A project the console still holds: watched now, or one a machine's sessions still report (a hub
 * that just restarted names it from their folder). A project removed with its machine is gone,
 * with its sessions: its old missions and landings never show as an "Unknown project" card (R-FN-8).
 */
export function nameable(input: Pick<FlowInput, 'projects' | 'sessions'>, id: Id): boolean {
  return !!input.projects[id] || input.sessions.some((s) => s.projectId === id);
}

/** A project as one fact: its active branches, else its agents at work, else the work waiting in its worktrees. */
function projectFact(w: WorktreeStates | undefined, working: number): FlowFact {
  if (w?.active) return { n: w.active, text: 'active' };
  if (working > 0) return { n: working, text: 'working' };
  if (w?.toLand) return { n: w.toLand, text: 'to land' };
  if (w?.uncommitted) return { n: w.uncommitted, text: 'uncommitted' };
  return { text: 'Quiet' };
}

/** The delivery stages, in order: a change is queued, verified, then lands or fails. */
const DELIVERY_STAGES = ['queued', 'verifying', 'landed', 'failed'] as const;
type DeliveryStage = (typeof DELIVERY_STAGES)[number];

/** What moves into the queue from a project: queued work (parked needs you), else work verifying, else nothing now. */
function entryState(d: Delivery): FlowState {
  if (d.queued) return d.parked ? 'attention' : 'pending';
  return d.verifying ? 'working' : 'idle';
}

/** Every project with anything in delivery: in flight, or landed or failed in the window. */
function deliveryProjects(input: FlowInput): Id[] {
  const ids = new Set<Id>();
  for (const p of input.pipelines) ids.add(p.projectId);
  for (const l of input.landing?.landings ?? []) ids.add(l.projectId);
  return [...ids].filter((id) => nameable(input, id));
}

/** How long something has waited, in words, for a tooltip line. */
const waitWords = (since: number | undefined, now: number): string | null => {
  if (since == null) return null;
  const m = Math.round((now - since) / 60_000);
  return m < 1 ? 'just now' : m < 120 ? `waiting ${m} min` : `waiting ${Math.round(m / 60)} h`;
};

/** What a failed landing's fate tag says, for its tooltip line (ORGU-83). */
const FATE_WORDS: Record<string, string> = {
  [LANDING_FATE.returned]: 'returned to its agent',
  [LANDING_FATE.stuck]: 'stuck, needs you',
};
const fateWords = (i: PipelineItem): string | null => i.tags?.map((t) => FATE_WORDS[t]).find(Boolean) ?? null;

/** The newest change titles in each delivery stage, newest first, for the hover tooltips. */
function deliveryTitles(input: FlowInput): Record<DeliveryStage, string[]> {
  const now = input.now ?? Date.now();
  const out: Record<DeliveryStage, Array<[number, string]>> = { queued: [], verifying: [], landed: [], failed: [] };
  const line = (title: string, note: string | null) => (note ? `${title}: ${note}` : title);
  for (const p of input.pipelines) {
    for (const i of p.items) {
      if (i.state === 'active') out.verifying.push([i.startedAt ?? 0, line(i.title, i.stage === AGENT_CHECKS_STAGE ? 'agent checking' : null)]);
      else if (i.state === 'queued') out.queued.push([i.startedAt ?? 0, line(i.title, waitWords(i.startedAt, now))]);
    }
    const all = [...p.items, ...p.recent];
    for (const i of p.recent) {
      if (!inWindow(i.endedAt, now)) continue;
      if (i.state === 'passed') out.landed.push([i.endedAt ?? 0, i.title]);
      else if (i.state === 'failed' && !landedSince(i) && !supersededFailure(i, all)) out.failed.push([i.endedAt ?? 0, line(i.title, fateWords(i))]);
    }
  }
  for (const l of input.landing?.landings ?? []) {
    if (l.status === 'queued' || l.status === 'parked') out.queued.push([l.submittedAt, line(l.title, waitWords(l.submittedAt, now))]);
    else if (l.status === 'active') out.verifying.push([l.submittedAt, l.title]);
    else if ((l.status === 'landed' || l.status === 'failed') && inWindow(l.endedAt, now)) out[l.status].push([l.endedAt ?? 0, l.title]);
  }
  const top = (xs: Array<[number, string]>) => [...xs].sort((a, b) => b[0] - a[0]).slice(0, 3).map(([, t]) => t);
  return { queued: top(out.queued), verifying: top(out.verifying), landed: top(out.landed), failed: top(out.failed) };
}

const counted = (n: number, one: string, many: string) => `${n} ${plural(n, one, many)}`;

/** What one delivery stage counts in a source. */
const STAGE_COUNT: Record<DeliveryStage, keyof Delivery> = { queued: 'queued', verifying: 'verifying', landed: 'landed', failed: 'failed' };

/** A stage card opens the source holding most of it (the list it is in), else the first source. */
function stageHref(sources: readonly DeliverySource[], st: DeliveryStage): string {
  const k = STAGE_COUNT[st];
  const best = [...sources].sort((a, b) => b[k] - a[k])[0];
  if (!best) return '/pipelines';
  if (best.href === '/pipelines/landing') return best.href;
  return `${best.href}&list=${st === 'queued' || st === 'verifying' ? 'now' : 'history'}`;
}

/** The sources behind a stage's number, one line each: "Orgus landings: 2". */
function stageBreakdown(sources: readonly DeliverySource[], st: DeliveryStage): string[] {
  const k = STAGE_COUNT[st];
  return sources.filter((s) => s[k] > 0).map((s) => `${s.label}: ${s[k]}`);
}

export function buildFlow(input: FlowInput): Flow {
  const now = input.now ?? Date.now();
  const live = input.sessions.filter((s) => s.status !== 'ended');
  // Sessions that ended today: the agents' past.
  const finished = input.sessions.filter((s) => s.status === 'ended' && inWindow(s.lastActivity, now));
  const read = new Map(live.map((s) => [s.id, sessionRead(s, input.needs)] as const));
  const trees = worktreesOf(input, live);
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const edge = (from: string, to: string, state: FlowState) => edges.push({ from, to, state, active: state === 'working' });
  const byKind = (list: readonly Session[]) => {
    const m = new Map<string, FlowState>();
    for (const s of list) m.set(agentKindOf(s), worst(m.get(agentKindOf(s)) ?? 'idle', STATE_OF[read.get(s.id)!]));
    return m;
  };
  const quiet = (column: FlowColumn, label: string, text: string, href: string): FlowNode => ({
    id: `empty:${column}`,
    column,
    kind: 'empty',
    label,
    fact: { text },
    detail: text,
    state: 'idle',
    hue: 'neutral',
    weight: 0,
    href,
  });

  // Projects: the branches at work in each (a session on them), joined to the agents at work there.
  const projects = activeProjects(input, live, trees, finished);
  const agentsFrom = new Map<Id, Map<string, FlowState>>();
  const landedIn = new Map<Id, number>();
  for (const s of deliverySources(input)) landedIn.set(s.projectId, (landedIn.get(s.projectId) ?? 0) + s.landed);
  for (const id of projects) {
    const sessions = live.filter((s) => s.projectId === id);
    const blocked = sessions.filter((s) => read.get(s.id) === 'blocked').length;
    const working = sessions.filter((s) => s.status === 'working').length;
    const w = trees.get(id);
    const fact = projectFact(w, working);
    nodes.push({
      id: `project:${id}`,
      projectId: id,
      column: 0,
      kind: 'project',
      label: projectNameOf(input, id),
      fact,
      detail: text(fact),
      state: blocked ? 'attention' : working ? 'working' : w?.active ? 'pending' : 'idle',
      hue: projectHue(id),
      tip: [
        ...(w && worktreeLine(w) ? [`Worktrees: ${worktreeLine(w)}`] : []),
        counted(working, 'agent working', 'agents working'),
        needsYouText(blocked),
        ...(landedIn.get(id) ? [`${landedIn.get(id)} landed ${WINDOW_WORD}`] : []),
      ],
      weight: working + (w?.active ?? 0) + blocked,
      ...(landedIn.get(id) ? { past: { n: landedIn.get(id)!, label: `landed ${WINDOW_WORD}` } } : {}),
      href: `/projects?project=${encodeURIComponent(id)}`,
    });
    agentsFrom.set(id, byKind(sessions));
  }
  if (!projects.size) nodes.push(quiet(0, 'Projects', 'No work in progress', '/projects'));

  // Agents, by kind: the glyph, the name and how many sessions; what needs you first. The
  // subagents and workflow agents its sessions run count in (the Agents page's Running count).
  const kinds = new Map<string, Session[]>();
  for (const s of live) kinds.set(agentKindOf(s), [...(kinds.get(agentKindOf(s)) ?? []), s]);
  const doneBy = new Map<string, number>();
  for (const s of finished) doneBy.set(agentKindOf(s), (doneBy.get(agentKindOf(s)) ?? 0) + 1);
  for (const [k, list] of kinds) {
    const done = doneBy.get(k) ?? 0;
    const tally = (x: SessionRead) => list.filter((s) => read.get(s.id) === x).length;
    const [blocked, working, asked, idle] = [tally('blocked'), tally('working'), tally('asked'), tally('idle')];
    const kids = list.map((s) => ({ subagents: s.subagents ?? [], workflows: s.workflows ?? [] }));
    const units = kids.reduce((n, s) => n + runningUnits(s), 0);
    const subs = kids.reduce((n, s) => n + s.subagents.filter((a) => a.status === 'running').length, 0);
    const runs = kids.reduce((n, s) => n + s.workflows.filter((w) => w.status === 'running').length, 0);
    const also = units ? { n: units, text: plural(units, 'subagent', 'subagents') } : undefined;
    const fact: FlowFact = blocked
      ? needsYouFact(blocked)
      : working
        ? { n: working, text: 'working', ...(also ? { also } : {}) }
        : asked
          ? { n: asked, text: 'asked' }
          : units
            ? { n: units, text: plural(units, 'subagent running', 'subagents running') }
            : { text: 'Idle' };
    const at = new URLSearchParams({ agent: k, ...(working ? { status: 'working' } : {}) });
    nodes.push({
      id: `agent:${k}`,
      column: 1,
      kind: 'agent',
      label: agentKindLabel(k),
      fact,
      detail: text(fact),
      state: blocked ? 'attention' : working || units ? 'working' : 'idle',
      hue: 'neutral',
      count: list.length,
      agentKind: k,
      live: working > 0 || units > 0,
      tip: [
        `${working} working`,
        needsYouText(blocked),
        ...(asked ? [`${asked} asked`] : []),
        `${idle} idle`,
        ...(subs ? [counted(subs, 'subagent running', 'subagents running')] : []),
        ...(runs ? [counted(runs, 'workflow running', 'workflows running')] : []),
        ...(done ? [`${done} finished ${WINDOW_WORD}`] : []),
      ],
      weight: list.length + units,
      ...(done ? { past: { n: done, label: `finished ${WINDOW_WORD}` } } : {}),
      href: `/sessions?${at.toString()}`,
    });
  }
  // A kind whose sessions all finished today: a quiet card that says so, from the projects they ran in.
  for (const [k, done] of doneBy) {
    if (kinds.has(k)) continue;
    const fact: FlowFact = { n: done, text: `finished ${WINDOW_WORD}` };
    nodes.push({
      id: `agent:${k}`,
      column: 1,
      kind: 'agent',
      label: agentKindLabel(k),
      fact,
      detail: text(fact),
      state: 'idle',
      hue: 'neutral',
      agentKind: k,
      tip: [`${done} finished ${WINDOW_WORD}`, 'None live now'],
      weight: 0,
      href: `/sessions?${new URLSearchParams({ agent: k, status: 'ended' }).toString()}`,
    });
    for (const pid of new Set(finished.filter((s) => agentKindOf(s) === k && s.projectId && projects.has(s.projectId)).map((s) => s.projectId!))) edge(`project:${pid}`, `agent:${k}`, 'idle');
  }
  const agentIds = kinds.size ? [...kinds.keys()].map((k) => `agent:${k}`) : doneBy.size ? [] : ['empty:1'];
  if (!kinds.size && !doneBy.size) nodes.push(quiet(1, 'Agents', 'No agents running', '/sessions'));
  for (const [id, m] of agentsFrom) {
    if (m.size) for (const [k, state] of m) edge(`project:${id}`, `agent:${k}`, state);
    else if (!kinds.size && !doneBy.size) edge(`project:${id}`, 'empty:1', 'idle');
  }

  // What is lined up and under way in Missions: a quiet Next card at the start of the chain
  // (missions planned, backlog items ready), and the missions running beside the agents.
  const ms = input.missions ?? [];
  const mcount = (s: MissionState) => ms.filter((m) => m.state === s).length;
  const [planned, running, waiting] = [mcount('planned'), mcount('running'), mcount('waiting')];
  const doneToday = ms.filter((m) => m.state === 'done' && inWindow(m.finishedAt, now)).length;
  const failedToday = ms.filter((m) => m.state === 'failed' && inWindow(m.finishedAt, now)).length;
  const ready = input.ready?.length ?? 0;
  const hasNext = planned + ready > 0;
  const hasMissions = hasNext || running + waiting + doneToday + failedToday > 0;
  if (hasNext) {
    const fact: FlowFact = planned ? { n: planned, text: 'planned', ...(ready ? { also: { n: ready, text: 'ready', keep: true } } : {}) } : { n: ready, text: 'ready' };
    nodes.push({
      id: 'next',
      column: 0,
      kind: 'next',
      label: 'Up next',
      fact,
      detail: text(fact),
      state: 'pending',
      hue: 'neutral',
      tip: [counted(planned, 'mission planned', 'missions planned'), counted(ready, 'backlog item ready', 'backlog items ready')],
      weight: planned + ready,
      href: planned ? '/missions?tab=missions' : '/missions?status=ready',
    });
  }
  if (hasMissions) {
    // A waiting mission is its session waiting, which the agent card already counts as needs you:
    // here it is work under way, and its tooltip says who waits.
    const paused = !!input.paused;
    const fact: FlowFact = paused ? { text: running + waiting ? `Paused, ${running + waiting} finishing` : 'Paused' } : running + waiting ? { n: running + waiting, text: 'under way' } : { text: 'None running' };
    nodes.push({
      id: 'missions',
      // Under Up next, not among the agents: a mission is work (a goal Orgus runs), not who does it.
      column: 0,
      kind: 'missions',
      label: 'Missions',
      fact,
      detail: text(fact),
      state: running ? 'working' : waiting ? 'pending' : 'idle',
      hue: 'neutral',
      ...(running + waiting ? { count: running + waiting } : {}),
      tip: [
        ...(paused ? ['Missions are paused: nothing new starts and queued work waits'] : []),
        `${running} running`,
        needsYouText(waiting),
        ...(planned ? [counted(planned, 'planned', 'planned')] : []),
        `${doneToday} done ${WINDOW_WORD}`,
        ...(failedToday ? [`${failedToday} failed ${WINDOW_WORD}`] : []),
      ],
      weight: running + waiting,
      ...(doneToday ? { past: { n: doneToday, label: `done ${WINDOW_WORD}` } } : {}),
      href: '/missions?tab=missions',
    });
    if (hasNext) edge('next', 'missions', planned && !paused ? 'pending' : 'idle');
  }
  if (!projects.size) for (const id of agentIds) edge('empty:0', id, 'idle');

  // Delivery: queued, verifying, landed and failed today (failed only when something did), joined
  // in order. Each card opens the pipeline that holds most of it; its tooltip names every source.
  const sources = deliverySources(input);
  const d = deliveryOf(input);
  const cpuStats = getFleetCpuWaitingStats(input.machineLoad);
  const delivery = sources.length > 0 || (input.landing?.queues.length ?? 0) > 0;
  const titles = deliveryTitles(input);
  if (cpuStats.totalWaiting > 0) {
    titles.queued.unshift(`CPU full: ${cpuStats.totalWaiting} waiting, next slot in about ${cpuStats.nextSlotMinutes} min`);
  }
  const stage = (st: DeliveryStage, label: string, n: number, state: FlowState, empty: string, fact?: FlowFact) => {
    const f: FlowFact = !n ? { text: empty } : (fact ?? { n, text: plural(n, 'change', 'changes') });
    const tip = [...(sources.length > 1 ? stageBreakdown(sources, st) : []), ...titles[st]];
    nodes.push({
      id: `stage:${st}`,
      column: 2,
      kind: 'stage',
      label,
      fact: f,
      detail: text(f),
      state: n ? state : 'idle',
      hue: n ? toneOf(state) : 'neutral',
      tip: tip.length ? tip : undefined,
      weight: n,
      href: stageHref(sources, st),
    });
  };
  if (delivery) {
    const queuedText = cpuStats.totalWaiting > 0 && d.queued === 0 ? `CPU full: ${cpuStats.totalWaiting} waiting` : 'Empty';
    const queuedFact = d.parked
      ? needsYouFact(d.parked)
      : cpuStats.totalWaiting > 0 && d.queued === 0
        ? { n: cpuStats.totalWaiting, text: 'waiting for CPU' }
        : { n: d.queued, text: 'waiting' };
    const queuedCount = d.queued || cpuStats.totalWaiting;
    const queuedState = d.parked ? 'attention' : queuedCount ? 'pending' : 'idle';
    stage('queued', 'Queued', queuedCount, queuedState, queuedText, queuedFact);
    stage('verifying', 'Verifying', d.verifying, 'working', 'Nothing running');
    stage('landed', 'Landed', d.landed, 'success', `None ${WINDOW_WORD}`, { n: d.landed, text: WINDOW_WORD });
    // Failed counts only failures not landed since; a stuck one needs you (ORGU-83).
    if (d.failed) stage('failed', 'Failed', d.failed, d.stuck ? 'attention' : 'danger', '', d.stuck ? { n: d.stuck, text: 'stuck', tone: 'attention' } : { n: d.failed, text: WINDOW_WORD, tone: 'danger' });
    // A project's changes enter the queue from its agents (or from the project when no agent is
    // live there); from the queue they are verified, then land or fail.
    for (const pid of deliveryProjects(input)) {
      const from = [...(agentsFrom.get(pid)?.keys() ?? [])].map((k) => `agent:${k}`);
      if (!from.length && projects.has(pid)) from.push(`project:${pid}`);
      const state = entryState(deliveryOf(input, pid));
      for (const f of from) edge(f, 'stage:queued', state);
    }
    if (hasMissions) edge('missions', 'stage:queued', running ? 'working' : 'idle');
    edge('stage:queued', 'stage:verifying', d.verifying ? 'working' : 'idle');
    edge('stage:verifying', 'stage:landed', d.landed ? 'success' : 'idle');
    if (d.failed) edge('stage:verifying', 'stage:failed', 'danger');
  } else {
    nodes.push(quiet(2, 'Landing queue', 'No landing queue yet', '/pipelines'));
    for (const id of agentIds) edge(id, 'empty:2', 'idle');
    if (hasMissions) edge('missions', 'empty:2', 'idle');
  }
  const landed = delivery ? 'stage:landed' : 'empty:2';

  // Environments: the landing module's (the newest deployment to each) and the projects' own
  // deploy targets; production first and a little brighter. With none, landed changes live on the
  // main branch: the flow still ends in a block.
  const envs = environmentsOf(input);
  for (const e of envs) {
    nodes.push({
      id: e.id,
      column: 3,
      kind: 'env',
      label: e.label,
      fact: e.fact,
      detail: text(e.fact),
      state: e.state,
      hue: toneOf(e.state),
      emphasis: e.emphasis,
      // Deploys done today ride in the tooltip: a version string leaves the card no room for more.
      tip: e.deploys ? [...(e.tip ?? []), counted(e.deploys, `deploy ${WINDOW_WORD}`, `deploys ${WINDOW_WORD}`)] : e.tip,
      weight: 0,
      href: '/pipelines/environments',
    });
    edge(landed, e.id, e.deployed ? e.state : 'idle');
  }
  if (!envs.length) {
    nodes.push({ ...quiet(3, 'Main', 'Landed changes live on main', '/pipelines'), id: 'env:main', kind: 'env' });
    edge(landed, 'env:main', d.landed ? 'success' : 'idle');
  }

  const inColumn = (c: FlowColumn) => nodes.filter((n) => n.column === c && n.kind !== 'empty' && n.kind !== 'next' && n.kind !== 'missions' && n.id !== 'env:main').length;
  return {
    ...fold(nodes, edges),
    totals: [inColumn(0), live.length, d.queued + d.verifying, inColumn(3)],
  };
}

/**
 * At most COLUMN_CAP nodes a column: what needs you, then failed, working, pending and the rest,
 * the busiest first, stay (Delivery and Environments keep their order). Past it, those and a
 * "+N more" that takes over the edges of what it holds, so the flow stays whole.
 */
function fold(nodes: FlowNode[], edges: FlowEdge[]): Omit<Flow, 'totals'> {
  const alias: Record<string, string> = {};
  const out: FlowNode[] = [];
  for (const col of [0, 1, 2, 3] as const) {
    // Next and Missions are pinned at the top of their columns, never folded.
    const pinned = nodes.filter((n) => n.column === col && (n.kind === 'next' || n.kind === 'missions'));
    out.push(...pinned);
    const list = nodes.filter((n) => n.column === col && n.kind !== 'next' && n.kind !== 'missions');
    const sorted = col >= 2 ? list : [...list].sort((a, b) => RANK[a.state] - RANK[b.state] || b.weight - a.weight || a.label.localeCompare(b.label));
    const cap = COLUMN_CAP[col] - pinned.length;
    // Projects and agents keep a steady order by name, so live state changes never reshuffle them
    // under the pointer; urgency only decides which ones stay out of "+N more".
    const steady = (xs: FlowNode[]) => (col <= 1 ? [...xs].sort((a, b) => a.label.localeCompare(b.label)) : xs);
    if (sorted.length <= cap) {
      out.push(...steady(sorted));
      continue;
    }
    const shown = steady(sorted.slice(0, cap - 1));
    const rest = sorted.slice(cap - 1);
    const id = `more:${col}`;
    for (const n of rest) alias[n.id] = id;
    const blocked = rest.reduce((n, x) => n + (x.fact.tone === 'attention' ? (x.fact.n ?? 1) : 0), 0);
    const fact: FlowFact = blocked
      ? needsYouFact(blocked)
      : {
          text: rest
            .slice(0, 2)
            .map((n) => n.label)
            .join(', '),
        };
    out.push(...shown, {
      id,
      column: col,
      kind: 'more',
      label: `+${rest.length} more`,
      fact,
      detail: text(fact),
      state: rest.reduce<FlowState>((s, n) => worst(s, n.state), 'idle'),
      hue: 'neutral',
      weight: 0,
      href: FLOW_STAGES[col].href,
    });
  }
  const merged = new Map<string, FlowEdge>();
  for (const e of edges) {
    const from = alias[e.from] ?? e.from;
    const to = alias[e.to] ?? e.to;
    const key = `${from}>${to}`;
    const prev = merged.get(key);
    merged.set(key, {
      from,
      to,
      state: prev ? worst(prev.state, e.state) : e.state,
      active: (prev?.active ?? false) || e.active,
    });
  }
  return { nodes: out, edges: [...merged.values()], alias };
}

// ---------------------------------------------------------------------------
// By project: one lane per project across the same stages

export interface Lane {
  projectId: Id;
  label: string;
  hue: DataSlot;
  /** "3 branches", "2 working" or "Quiet". */
  meta: string;
  agents: { working: number; needs: number; total: number; state: FlowState };
  delivery: Delivery & { state: FlowState };
  /** The environment worth showing (deploying or failed first, then production, then the newest). */
  env: { name: string | null; text: string; state: FlowState };
  /** The line into each stage (Agents, Delivery, Environments): the state of what moves into it. */
  links: [FlowState, FlowState, FlowState];
  needs: number;
  busy: number;
}

export function buildLanes(input: FlowInput, max = MAX_LANES): { lanes: Lane[]; more: number } {
  const live = input.sessions.filter((s) => s.status !== 'ended');
  const trees = worktreesOf(input, live);
  const now = input.now ?? Date.now();
  const ids = activeProjects(
    input,
    live,
    trees,
    input.sessions.filter((s) => s.status === 'ended' && inWindow(s.lastActivity, now)),
  );
  const lanes: Lane[] = [];
  for (const id of ids) {
    const reads = live.filter((s) => s.projectId === id).map((s) => sessionRead(s, input.needs));
    const needs = reads.filter((r) => r === 'blocked').length;
    const working = reads.filter((r) => r === 'working').length;
    const w = trees.get(id);
    const branches = w?.active ?? 0;
    const d = deliveryOf(input, id);
    const deliveryState: FlowState = d.parked || d.stuck ? 'attention' : d.verifying ? 'working' : d.queued ? 'pending' : d.failed ? 'danger' : d.landed ? 'success' : 'idle';
    const envs = environmentsOf(input, id);
    const shown = envs.find((e) => e.state === 'working' || e.state === 'danger') ?? envs.find((e) => e.emphasis && e.deployed) ?? envs.find((e) => e.deployed);
    const agentsState: FlowState = needs ? 'attention' : working ? 'working' : 'idle';
    const envState = shown?.state ?? 'idle';
    lanes.push({
      projectId: id,
      label: projectNameOf(input, id),
      hue: projectHue(id),
      meta: text(projectFact(w, working)),
      agents: { working, needs, total: reads.length, state: agentsState },
      delivery: {
        queued: d.queued,
        verifying: d.verifying,
        landed: d.landed,
        failed: d.failed,
        parked: d.parked,
        stuck: d.stuck,
        state: deliveryState,
      },
      env: {
        name: shown ? shown.label : null,
        text: shown ? text(shown.fact) : 'Not deployed',
        state: envState,
      },
      links: [agentsState, deliveryState, envState],
      needs: needs + d.parked + d.stuck,
      busy: working + branches + d.queued + d.verifying,
    });
  }
  lanes.sort((a, b) => b.needs - a.needs || b.busy - a.busy || a.label.localeCompare(b.label));
  return { lanes: lanes.slice(0, max), more: Math.max(0, lanes.length - max) };
}

// ---------------------------------------------------------------------------
// Events: which node (Overall) or which lane and stage (By project) an event moves

/** Where an event lands: the stage column and project it concerns, its node, and where its dot may come from. */
export interface FlowEvent {
  projectId: Id | null;
  column: FlowColumn;
  node: string;
  /** Candidate nodes the dot comes from, best first; empty: any line into the node. */
  from: string[];
  /** Ripple and tint only, no dot: something left (a session ended), nothing arrived. */
  still?: boolean;
}

/** An event placed in the Overall graph: the node to ripple and the node its dot leaves (null: ripple only). */
export interface FlowHit {
  node: string;
  from: string | null;
}

export interface EventContext {
  sessions: Record<Id, Session>;
  pipelines: readonly Pipeline[];
}

function sessionEvent(s: Session): FlowEvent {
  return {
    projectId: s.projectId,
    column: 1,
    node: `agent:${agentKindOf(s)}`,
    from: s.projectId ? [`project:${s.projectId}`] : [],
  };
}

/** Where a project's change comes from: the agents live in the project, then the project itself. */
function changeSources(projectId: Id, sessions: Record<Id, Session> | undefined): string[] {
  const kinds = new Set<string>();
  for (const s of Object.values(sessions ?? {})) if (s.projectId === projectId && s.status !== 'ended') kinds.add(`agent:${agentKindOf(s)}`);
  return [...kinds, `project:${projectId}`];
}

/** A change reaching a delivery stage: into the queue from an agent at work on its project, then along the chain. */
function stageEvent(projectId: Id, stage: DeliveryStage, sessions: Record<Id, Session> | undefined): FlowEvent {
  const from = stage === 'queued' ? changeSources(projectId, sessions) : stage === 'verifying' ? ['stage:queued'] : ['stage:verifying'];
  return { projectId, column: 2, node: `stage:${stage}`, from };
}

const ITEM_STAGE: Partial<Record<string, DeliveryStage>> = { queued: 'queued', active: 'verifying', passed: 'landed', failed: 'failed' };

/**
 * Which stage a feed event belongs to. A session that starts, asks or works (a subagent or workflow
 * starting or finishing): its agent, from its project. A checkout that moved: its project. A
 * landing queue item: the stage it reached, from the agents at work on its project. Anything else
 * moves nothing.
 */
export function eventOf(e: Pick<FeedEvent, 'kind' | 'projectId' | 'ref'>, ctx: EventContext): FlowEvent | null {
  const session = e.ref?.type === 'session' ? ctx.sessions[e.ref.id] : undefined;
  switch (e.kind) {
    case 'session.started':
    case 'session.waiting':
    case 'subagent.started':
    case 'subagent.finished':
    case 'workflow.started':
    case 'workflow.finished':
      return session ? sessionEvent(session) : null;
    case 'checkout.changed':
      return e.projectId
        ? {
            projectId: e.projectId,
            column: 0,
            node: `project:${e.projectId}`,
            from: [],
          }
        : null;
    case 'pipeline.item': {
      if (e.ref?.type !== 'pipelineItem') return null;
      for (const p of ctx.pipelines) {
        const item = p.items.find((i) => i.id === e.ref!.id) ?? p.recent.find((i) => i.id === e.ref!.id);
        if (!item) continue;
        const stage = ITEM_STAGE[item.state];
        return stage ? stageEvent(p.projectId, stage, ctx.sessions) : null;
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * What the session patches say happened (they carry no feed event for most of it): a session that
 * appeared, started working, made more tool calls while it works, or ended (its count moves into
 * the past). Each lights its agent. Nothing on the first reading (an empty `prev`), so a page load
 * never replays history.
 */
export function sessionEvents(prev: Record<Id, Session>, next: Record<Id, Session>): FlowEvent[] {
  const out: FlowEvent[] = [];
  let seen = false;
  for (const _ in prev) {
    seen = true;
    break;
  }
  if (!seen) return out;
  for (const s of Object.values(next)) {
    const was = prev[s.id];
    if (was === s) continue;
    if (!was) {
      if (s.status !== 'ended') out.push(sessionEvent(s));
      continue;
    }
    if (s.status === 'ended') {
      if (was.status !== 'ended') out.push({ ...sessionEvent(s), from: [], still: true });
      continue;
    }
    if (s.status !== 'working') continue;
    if (was.status !== 'working' || (s.toolCalls ?? 0) > (was.toolCalls ?? 0)) out.push(sessionEvent(s));
  }
  return out;
}

/**
 * The pipelines' changes between two readings, for every adapter (the agent's feed announces an
 * item only when it finishes): a change that entered the queue, started verifying, landed or
 * failed. Finished items are matched by what is new in `recent`, so an adapter that gives a change
 * another id once it finishes (the Orgus landings) still moves along the chain. A pipeline seen
 * for the first time moves nothing.
 */
export function pipelineEvents(prev: readonly Pipeline[], next: readonly Pipeline[], sessions?: Record<Id, Session>): FlowEvent[] {
  if (prev === next) return [];
  const before = new Map(prev.map((p) => [pipelineKey(p), p]));
  const out: FlowEvent[] = [];
  for (const p of next) {
    const q = before.get(pipelineKey(p));
    if (!q || q === p) continue;
    const was = new Map(q.items.map((i) => [i.id, i.state]));
    for (const i of p.items) {
      const s = was.get(i.id);
      if (i.state === 'queued' && s === undefined) out.push(stageEvent(p.projectId, 'queued', sessions));
      else if (i.state === 'active' && s !== 'active') out.push(stageEvent(p.projectId, 'verifying', sessions));
    }
    const done = new Set(q.recent.map((i) => i.id));
    for (const i of p.recent) {
      if (done.has(i.id)) continue;
      if (i.state === 'passed') out.push(stageEvent(p.projectId, 'landed', sessions));
      else if (i.state === 'failed') out.push(stageEvent(p.projectId, 'failed', sessions));
    }
  }
  return out;
}

/**
 * Missions' changes between two readings: a mission planned or an item made Ready (Next), and a
 * mission that started, waits, finished or failed (Missions, from Next). Nothing on the first reading.
 */
export function missionEvents(prev: { missions: readonly FlowMission[]; ready: number } | null, next: { missions: readonly FlowMission[]; ready: number }): FlowEvent[] {
  if (!prev || (prev.missions === next.missions && prev.ready === next.ready)) return [];
  const out: FlowEvent[] = [];
  const was = new Map(prev.missions.map((m) => [m.id, m.state]));
  for (const m of next.missions) {
    const s = was.get(m.id);
    if (s === m.state) continue;
    if (m.state === 'planned') out.push({ projectId: m.projectId, column: 0, node: 'next', from: [] });
    else if (s !== undefined || m.state === 'running') out.push({ projectId: m.projectId, column: 0, node: 'missions', from: s === 'planned' ? ['next'] : [] });
  }
  if (next.ready > prev.ready) out.push({ projectId: null, column: 0, node: 'next', from: [] });
  return out;
}

function landingEvent(l: Landing, before: Landing['status'] | undefined, sessions: Record<Id, Session> | undefined): FlowEvent | null {
  if (l.status === 'queued' && before === undefined) return stageEvent(l.projectId, 'queued', sessions);
  if (l.status === 'active') return stageEvent(l.projectId, 'verifying', sessions);
  if (l.status === 'landed') return stageEvent(l.projectId, 'landed', sessions);
  if (l.status === 'failed') return stageEvent(l.projectId, 'failed', sessions);
  return null;
}

/**
 * The landing module's changes between two readings: a landing that was queued, started
 * verifying, landed or failed, and a deployment that started, went live or failed. Nothing on the
 * first reading, so a page load never replays history.
 */
export function landingEvents(prev: LandingState | undefined, next: LandingState | undefined, sessions?: Record<Id, Session>): FlowEvent[] {
  if (!prev || !next || prev === next) return [];
  const out: FlowEvent[] = [];
  const was = new Map(prev.landings.map((l) => [l.id, l.status]));
  for (const l of next.landings) {
    const before = was.get(l.id);
    if (before === l.status) continue;
    const ev = landingEvent(l, before, sessions);
    if (ev) out.push(ev);
  }
  const deployed = new Map(prev.deployments.map((d) => [d.id, d.status]));
  for (const d of next.deployments) {
    if (deployed.get(d.id) === d.status) continue;
    if (d.status !== 'deploying' && d.status !== 'live' && d.status !== 'failed') continue;
    out.push({
      projectId: d.projectId,
      column: 3,
      node: envNodeId(d.env),
      from: ['stage:landed'],
    });
  }
  return out;
}

/** An event in the Overall graph: its node (or the "+N more" holding it) and the first drawn line into it. */
export function resolveHit(flow: Flow, ev: FlowEvent): FlowHit | null {
  const real = (id: string) => {
    const r = flow.alias[id] ?? id;
    return flow.nodes.some((n) => n.id === r) ? r : null;
  };
  const node = real(ev.node);
  if (!node) return null;
  if (ev.still) return { node, from: null };
  const into = flow.edges.filter((e) => e.to === node);
  if (!ev.from.length)
    return {
      node,
      from: (into.find((e) => e.active) ?? into[0])?.from ?? null,
    };
  for (const f of ev.from) {
    const src = real(f);
    if (src && into.some((e) => e.from === src)) return { node, from: src };
  }
  return { node, from: null };
}
